import { roomHeightRange } from "./room";
import { FIELD_SIZE, newId, type FieldKind, type Item, type ItemKind } from "./types";

export interface CatalogEntry {
  kind: ItemKind;
  label: string;
  hint: string;
}

export const CATALOG: CatalogEntry[] = [
  { kind: "house", label: "Casa", hint: "Clickeá el terreno para ubicar la casa" },
  { kind: "room", label: "Ambiente", hint: "Clickeá el terreno para el ambiente" },
  { kind: "pool", label: "Pileta", hint: "Clickeá el terreno para la pileta" },
  { kind: "field", label: "Cancha", hint: "Clickeá el terreno para la cancha" },
  { kind: "tree", label: "Árbol", hint: "Clickeá el terreno para plantar" },
  { kind: "toilet", label: "Inodoro", hint: "Clickeá el terreno para el inodoro" },
  { kind: "kitchen", label: "Cocina", hint: "Clickeá el terreno para la cocina" },
  { kind: "column", label: "Columna", hint: "Clickeá el terreno para la columna" },
  { kind: "canopy", label: "Techo", hint: "Clickeá el terreno: techo suelto (chapa o losa)" },
  {
    kind: "wall",
    label: "Pared",
    hint: "Clickeá cerca de una pared o ambiente: el extremo se pega. Con dos seleccionados, el 1er click pega al primero y el 2do al segundo",
  },
];

const DEFAULTS: Record<ItemKind, Omit<Item, "id" | "x" | "z" | "name">> = {
  house: {
    kind: "house",
    width: 12,
    depth: 16,
    height: 3.2,
    rotation: 0,
    roof: "tiles",
    wall: "stucco",
    openings: [
      { id: newId(), kind: "door", wall: 0, t: 0.5, height: 0 },
      { id: newId(), kind: "window", wall: 0, t: 0.25, height: 1.05 },
      { id: newId(), kind: "window", wall: 0, t: 0.75, height: 1.05 },
    ],
  },
  room: { kind: "room", width: 4, depth: 3.5, height: 2.6, rotation: 0 },
  pool: { kind: "pool", width: 8, depth: 4, height: 0.5, rotation: 0 },
  field: { kind: "field", width: 40, depth: 20, height: 0.1, rotation: 0, fieldKind: "f5" },
  tree: { kind: "tree", width: 2, depth: 2, height: 3.2, rotation: 0 },
  toilet: { kind: "toilet", width: 0.7, depth: 0.5, height: 0.8, rotation: 0 },
  kitchen: { kind: "kitchen", width: 2.4, depth: 0.7, height: 0.9, rotation: 0 },
  column: { kind: "column", width: 0.3, depth: 0.3, height: 2.6, rotation: 0 },
  canopy: { kind: "canopy", width: 6, depth: 4, height: 3.2, heightEnd: 2.6, rotation: 0, cover: "chapa" },
  wall: {
    kind: "wall",
    width: 4,
    depth: 0.15,
    height: 2.6,
    rotation: 0,
    hasDoor: false,
    doorWidth: 0.8,
    doorHeight: 2.1,
    doorOffset: 1.2,
    doorSill: 0,
  },
};

export function catalogLabel(kind: ItemKind): string {
  return CATALOG.find((c) => c.kind === kind)?.label ?? kind;
}

export function nextName(kind: ItemKind, items: Item[]): string {
  const base = catalogLabel(kind);
  const n = items.filter((i) => i.kind === kind).length + 1;
  return n === 1 ? base : `${base} ${n}`;
}

export function createItem(kind: ItemKind, x: number, z: number, parentId?: string): Item {
  const base = DEFAULTS[kind];
  const item: Item = {
    ...base,
    id: newId(),
    name: nextName(kind, []),
    x,
    z,
    openings: base.openings?.map((op) => ({ ...op, id: newId() })),
    apertures: base.apertures?.map((ap) => ({ ...ap, id: newId() })),
  };
  if (kind === "room" && parentId) item.parentId = parentId;
  if (kind === "field") {
    item.fieldKind = "f5";
    item.width = FIELD_SIZE.f5.width;
    item.depth = FIELD_SIZE.f5.depth;
    item.name = "Cancha F5";
  }
  return item;
}

export function applyFieldKind(item: Item, kind: FieldKind): void {
  item.fieldKind = kind;
  if (kind === "custom") {
    item.name = item.name.startsWith("Cancha") ? item.name : "Cancha";
    return;
  }
  item.width = FIELD_SIZE[kind].width;
  item.depth = FIELD_SIZE[kind].depth;
  item.name = `Cancha ${kind.toUpperCase()}`;
}

export function measureLabel(item: Item): string {
  if (item.kind === "tree") return `${item.name}`;
  if (item.kind === "column") {
    return `${item.name} ${item.width.toFixed(2)} × ${item.depth.toFixed(2)} · h ${item.height.toFixed(2)} m`;
  }
  if (item.kind === "wall") {
    const n = item.apertures?.length ?? (item.hasDoor ? 1 : 0);
    const extra = n ? ` · ${n} abertura${n === 1 ? "" : "s"}` : "";
    return `${item.name} ${item.width.toFixed(2)} × ${item.height.toFixed(2)} m${extra}`;
  }
  if (item.kind === "canopy") {
    const end = item.heightEnd ?? item.height;
    const mat = item.cover === "losa" ? "losa" : "chapa";
    return `${item.name} ${item.width.toFixed(1)} × ${item.depth.toFixed(1)} · ${mat} ${item.height.toFixed(2)}→${end.toFixed(2)} m`;
  }
  if (item.kind === "room") {
    const { min, max } = roomHeightRange(item);
    const h = min === max ? `${max.toFixed(2)} m` : `${min.toFixed(2)}→${max.toFixed(2)} m`;
    return `${item.name} ${item.width.toFixed(1)} × ${item.depth.toFixed(1)} · h ${h}`;
  }
  return `${item.name} ${item.width.toFixed(1)} × ${item.depth.toFixed(1)} m`;
}
